/**
 * PURCHASE EVENT TRACKING TOGGLE
 * Set to false to disable all purchase conversion tracking (GA4, Google Ads, etc.)
 */
const PURCHASE_EVENT_TRACKING_ENABLED = true;

class OrderCallbackController {
  constructor() {
    this.root = document.getElementById('order-callback-root');
    this.token = this.getToken();
    this.clearFlagKey = this.token ? `order-callback-cleared:${this.token}` : null;
  }

  async init() {
    if (!this.token) return;
    window.orderCallbackMode = true;
    window.orderCallbackToken = this.token;

    // Prevent back navigation after successful payment
    history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', () => {
      history.pushState(null, '', window.location.href);
    });

    // Clear all exchange/extend data immediately — payment is complete
    try {
      localStorage.removeItem('exchangeGiftCard');
      localStorage.removeItem('extendGiftCard');
      sessionStorage.setItem('exchange-completed', Date.now().toString());
    } catch (_) {}

    // GTM is loaded site-wide via gtm-head.liquid — no need to load here
    // Google Ads conversions now route through GTM → sGTM

    this.hideCartUI();
    this.ensureMounted();
    // Show loading immediately
    this.showLoadingState();
    // Remove the white overlay now that callback UI is ready
    const overlay = document.getElementById('callback-overlay');
    if (overlay) overlay.remove();
    // Clear cart immediately — payment already succeeded when Qliro redirects here
    this.clearCartIfNeeded();
    // Fetch and render order details in parallel
    await this.fetchAndRender();
  }

  isValidToken(t) {
    return typeof t === 'string' && t.length === 64 && /^[a-f0-9]{64}$/i.test(t);
  }

  // Normalize phone to E.164 format
  normalizePhone(phone) {
    if (!phone) return '';
    
    // Remove all non-digit characters
    let digits = phone.replace(/\D/g, '');
    
    // If it already starts with country code, add +
    if (digits.startsWith('46') && digits.length >= 10) {
      return '+' + digits;
    }
    
    // If it starts with 0 (Swedish national format), replace with +46
    if (digits.startsWith('0') && digits.length >= 10) {
      return '+46' + digits.substring(1);
    }
    
    // If it's just digits without country code, assume Sweden (+46)
    if (digits.length >= 9 && !digits.startsWith('46')) {
      return '+46' + digits;
    }
    
    // Return with + prefix if we have digits
    return digits ? '+' + digits : '';
  }

  // Core SHA-256 hash using SubtleCrypto API
  async sha256(message) {
    if (!message) return '';

    const str = message.toString();
    if (!str) return '';

    if (window.crypto && window.crypto.subtle) {
      try {
        const msgBuffer = new TextEncoder().encode(str);
        const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      } catch (e) {
        console.warn('Order Callback: crypto.subtle.digest failed:', e);
      }
    }

    console.warn('Order Callback: SHA256 not available, user data will not be hashed');
    return '';
  }

  // Hash email — matches server-side hashEmail():
  // lowercase, remove ALL spaces, strip dots from Gmail local part, then SHA-256
  async hashEmail(email) {
    if (!email) return '';
    let normalized = email.toLowerCase().replace(/\s/g, '');
    if (!normalized.includes('@')) return '';
    if (normalized.includes('@gmail.com') || normalized.includes('@googlemail.com')) {
      const [local, domain] = normalized.split('@');
      normalized = local.replace(/\./g, '') + '@' + domain;
    }
    return this.sha256(normalized);
  }

  // Hash name — matches server-side hashName():
  // remove digits and symbols (keep Unicode letters and spaces), lowercase, collapse spaces, trim, then SHA-256
  async hashName(name) {
    if (!name) return '';
    let normalized = name.replace(/[^\p{L}\s]/gu, ' ');
    normalized = normalized.toLowerCase().replace(/\s+/g, ' ').trim();
    if (!normalized) return '';
    return this.sha256(normalized);
  }

  // Hash street address — matches server-side hashStreetAddress():
  // remove symbols (keep letters, digits, spaces), lowercase, collapse spaces, trim, then SHA-256
  async hashStreet(street) {
    if (!street) return '';
    let normalized = street.toLowerCase().replace(/[^\p{L}\d\s]/gu, ' ');
    normalized = normalized.replace(/\s+/g, ' ').trim();
    if (!normalized) return '';
    return this.sha256(normalized);
  }

  // Convert full country name to ISO 3166-1 alpha-2 code
  countryToISO(name) {
    if (!name) return '';
    const map = {
      'sweden': 'SE', 'norway': 'NO', 'denmark': 'DK', 'finland': 'FI',
      'germany': 'DE', 'france': 'FR', 'spain': 'ES', 'italy': 'IT',
      'united kingdom': 'GB', 'united states': 'US', 'netherlands': 'NL',
      'belgium': 'BE', 'austria': 'AT', 'switzerland': 'CH', 'poland': 'PL',
      'portugal': 'PT', 'ireland': 'IE', 'iceland': 'IS', 'estonia': 'EE',
      'latvia': 'LV', 'lithuania': 'LT', 'czech republic': 'CZ', 'czechia': 'CZ',
      'slovakia': 'SK', 'hungary': 'HU', 'romania': 'RO', 'bulgaria': 'BG',
      'croatia': 'HR', 'slovenia': 'SI', 'greece': 'GR', 'cyprus': 'CY',
      'malta': 'MT', 'luxembourg': 'LU', 'canada': 'CA', 'australia': 'AU',
      'new zealand': 'NZ', 'japan': 'JP', 'south korea': 'KR', 'china': 'CN',
      'india': 'IN', 'brazil': 'BR', 'mexico': 'MX', 'türkiye': 'TR', 'turkey': 'TR'
    };
    return map[name.toLowerCase().trim()] || name;
  }

  getToken() {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromQuery = params.get('t');
      if (this.isValidToken(fromQuery)) return fromQuery;
      const parts = window.location.pathname.split('/').filter(Boolean);
      const cbIndex = parts.findIndex(p => p === 'callback');
      if (cbIndex !== -1 && this.isValidToken(parts[cbIndex + 1])) return parts[cbIndex + 1];
    } catch (_) {}
    return null;
  }

  ensureMounted() {
    if (!this.root) this.root = document.getElementById('order-callback-root');
    if (!this.root) return null;
    if (!this.root.hasChildNodes()) {
      const tpl = document.getElementById('order-callback-template');
      if (tpl && tpl.content) {
        this.root.appendChild(tpl.content.cloneNode(true));
      }
    }
    return this.root;
  }

  setHidden(el, hidden) {
    if (!el) return;
    el.setAttribute('aria-hidden', hidden ? 'true' : 'false');
  }

  hideCartUI() {
    const cartItemsEl = document.querySelector('cart-items');
    this.setHidden(cartItemsEl, true);
    this.setHidden(this.root, false);
  }

  fmtDate(iso) {
    try {
      return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (e) { return iso; }
  }

  toCents(amount) {
    const num = typeof amount === 'number' ? amount : parseFloat(amount);
    if (isNaN(num)) return 0;
    return Math.round(num * 100);
  }

  escapeAttr(val) {
    try { return String(val).replace(/"/g, '&quot;'); } catch (_) { return ''; }
  }

  escapeHtml(val) {
    try {
      return String(val)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    } catch (_) { return ''; }
  }

  /** Variant/product titles include unit price for the CDE picker ("Magic 39 kr"). */
  stripTrailingMoney(title) {
    return String(title || '').replace(/\s+\d+[\s\u00a0]*kr\.?$/i, '').trim();
  }

  setField(sel, value) {
    const el = document.querySelector(`[data-field="${sel}"]`);
    if (el) el.textContent = value || '';
  }

  getLineItemAttributes(item) {
    const map = Object.create(null);
    const attrs = Array.isArray(item?.customAttributes) ? item.customAttributes : [];
    for (const attr of attrs) {
      if (attr && attr.key) map[attr.key] = attr.value;
    }
    return map;
  }

  renderLineItems(items) {
    const list = document.querySelector('[data-list="items"]');
    if (!list) return;
    list.innerHTML = '';

    // Build quick lookup: group id -> main product title
    const groupToMainTitle = new Map();
    for (const it of items) {
      const map = this.getLineItemAttributes(it);
      const gid = map['_delivery_group_id'];
      const type = map['_item_type'];
      if (gid && !type && it?.title && !groupToMainTitle.has(gid)) {
        groupToMainTitle.set(gid, it.title);
      }
    }

    items.forEach(item => {
      const total = Shopify.formatMoney(this.toCents(item.totalPrice), window.theme.moneyFormat);
      const img = item?.product?.image;
        const media = img?.url
          ? `<div class="order-callback__item-media"><img src="${img.url}" alt="${this.escapeAttr(img.altText || item.title)}" loading="lazy" decoding="async"${img.width ? ` width="${img.width}"` : ''}${img.height ? ` height="${img.height}"` : ''}></div>`
          : `<div class="order-callback__item-media"></div>`;

      const map = this.getLineItemAttributes(item);
      const isGiftboxItem = map['_item_type'] === 'giftbox';
      const qty = parseInt(item.quantity, 10) || 1;
      const giftboxOptionKeys = new Set(['bow', 'ribbon', 'rosette', 'rosett']);

      // Giftbox ribbon lives on the variant. selectedOptions are also copied into
      // customAttributes (Bow + Rosette), so skip those or the same line prints twice.
      const attrs = Array.isArray(item.customAttributes) ? item.customAttributes : [];
      const attrsHtml = attrs
        .filter(a => a && (a.key || a.value))
        .filter(a => (a.key || '').charAt(0) !== '_')
        .filter(a => !(isGiftboxItem && giftboxOptionKeys.has((a.key || '').toLowerCase())))
        .map(a => `<div class="u-p4 order-callback__item-attr grey-primary-text"><span>${this.escapeHtml(a.key || '')}:</span> ${this.escapeHtml(a.value || '')}</div>`)
        .join('');

      let variantHtml = '';
      if (isGiftboxItem && item.variant?.title && item.variant.title !== 'Default Title') {
        const ribbonLabel = this.getTranslation('cart.delivery_ribbon_label', 'Ribbon');
        const ribbonName = this.stripTrailingMoney(item.variant.title);
        variantHtml = `<div class="u-p4 order-callback__item-attr grey-primary-text"><span>${this.escapeHtml(ribbonLabel)}:</span> ${this.escapeHtml(ribbonName)}</div>`;
      }

      // If item is giftbox/bow with delivery group, append "part of (main product)"
      let partOfHtml = '';
      try {
        const gid = map['_delivery_group_id'];
        const type = (map['_item_type'] || '').toLowerCase();
        if (gid && (type === 'giftbox' || type === 'bow')) {
          const mainTitle = groupToMainTitle.get(gid);
          if (mainTitle) {
            const base = this.getTranslation('customer.order.callback.part_of', 'Part of {{ product }}');
            const text = base.replace('{{ product }}', mainTitle);
            partOfHtml = `<div class="u-p4 grey-primary-text">${this.escapeHtml(text)}</div>`;
          }
        }
      } catch (_) {}

      const productUrl = item.product?.handle ? `/products/${item.product.handle}` : '#';

      // For giftbox items, show product title only (not variant title with bow name)
      // For other items, show full item title
      let displayTitle = isGiftboxItem && item.product?.title ? item.product.title : item.title;
      if (isGiftboxItem) displayTitle = this.stripTrailingMoney(displayTitle);
      const qtyHtml = qty > 1 || isGiftboxItem
        ? `<span class="u-s3"> x${qty}</span>`
        : '';

      // Render gift message(s) (CDE) for experience items
      let giftMessageHtml = '';
      if (!isGiftboxItem) {
        const label = this.getTranslation('cart.gift_message', 'Gift message');
        let messages = [];
        if (map['_gift_messages']) {
          try {
            const parsed = JSON.parse(map['_gift_messages']);
            if (Array.isArray(parsed)) messages = parsed.map((m) => String(m || '').trim()).filter(Boolean);
          } catch (_) { /* use single message */ }
        }
        if (!messages.length && map['_gift_message']) messages = [map['_gift_message']];
        giftMessageHtml = messages.map((msg) =>
          `<div class="u-p4 order-callback__item-attr grey-primary-text"><span>${this.escapeHtml(label)}:</span> ${this.escapeHtml(msg)}</div>`
        ).join('');
      }

      // Combine variant info and custom attributes
      const combinedAttrsHtml = variantHtml + attrsHtml + giftMessageHtml;

      const el = document.createElement('a');
      el.href = productUrl;
      el.className = 'order-callback__item';
      el.innerHTML = `
        ${media || ''}
        <div class="order-callback__item-content">
          <div>
            <div class="u-s3">${this.escapeHtml(displayTitle || '')}${qtyHtml}</div>
            ${partOfHtml || ''}
            ${combinedAttrsHtml ? `<div class="order-callback__item-attrs">${combinedAttrsHtml}</div>` : ''}
          </div>
          <div class="u-s3" style="flex-shrink: 0">${total || ''}</div>
        </div>`;

      list.appendChild(el);
    });
  }

  renderAddress(addr, customer) {
    const container = document.querySelector('[data-field="shipping-address"]');
    if (!container) return;
    if (!addr) { container.innerHTML = '<p class="u-p3">—</p>'; return; }
    const nameFromAddr = (addr.fullName && String(addr.fullName).trim())
      || `${addr.firstName || ''} ${addr.lastName || ''}`.trim();
    const nameFromCustomer = customer ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim() : '';
    const nameLineVal = nameFromAddr || nameFromCustomer || '';
    const lines = [
      nameLineVal,
      [addr.address1, addr.address2].filter(Boolean).join(' '),
      [addr.city, addr.province, addr.zip].filter(Boolean).join(', '),
      addr.country
    ].filter(Boolean);
    const nameLine = lines.shift() || '';
    const rest = lines;
    const restHtml = rest.map(l => `<div class=\"u-p3 grey-primary-text\">${this.escapeHtml(l)}</div>`).join('');
    container.innerHTML = `<address>${nameLine ? `<div class=\"u-s3\">${this.escapeHtml(nameLine)}</div>` : ''}${restHtml}</address>`;
  }

  renderSummary(pricing, discounts, giftCards) {
    // Helper function to set field or remove row
    const updateRow = (fieldName, value, selector) => {
      const numberValue = parseFloat(value);
      const row = document.querySelector(selector);
      if (numberValue > 0) {
        this.setField(fieldName, Shopify.formatMoney(this.toCents(numberValue), window.theme.moneyFormat));
      } else if (row) {
        row.remove();
      }
    };

    this.setField('summary-subtotal', Shopify.formatMoney(this.toCents(pricing.subtotal), window.theme.moneyFormat));

    if (pricing.shippingTitle) {
      const labelEl = document.querySelector('[data-field="summary-shipping-label"]');
      if (labelEl) labelEl.textContent = pricing.shippingTitle;
    }
    updateRow('summary-shipping', pricing.shipping, ".order-callback__row.shipping");
    updateRow('summary-tax', pricing.tax, ".order-callback__row.tax");

    // Handle discounts
    if (discounts && discounts.totalAmount) {
      const discountAmount = parseFloat(discounts.totalAmount);
      if (discountAmount > 0) {
        const discountRow = document.querySelector('.order-callback__row.discount');
        if (discountRow) {
          // Check if discount includes a gift card by examining the description field
          // Description contains "Applied Gift Cards: GC GID:" when gift cards are involved
          const description = discounts.description || '';
          const title = discounts.title || '';
          const code = discounts.code || '';
          
          const isGiftCard = description.toLowerCase().includes('applied gift card') || 
                           description.toLowerCase().includes('gift cards:');
          
          // Update label based on type
          const labelField = discountRow.querySelector('[data-field="summary-discount-label"]');
          if (labelField) {
            if (isGiftCard) {
              labelField.textContent = this.getTranslation('customer.order.gift_card_applied', 'Gift Card Applied');
            } else {
              labelField.textContent = this.getTranslation('customer.order.discount', 'Discount');
            }
          }
          
          const discountField = discountRow.querySelector('[data-field="summary-discount"]');
          if (discountField) {
            discountField.textContent = '-' + Shopify.formatMoney(this.toCents(discountAmount), window.theme.moneyFormat);
          }
        }
      } else {
        const discountRow = document.querySelector('.order-callback__row.discount');
        if (discountRow) discountRow.remove();
      }
    } else {
      const discountRow = document.querySelector('.order-callback__row.discount');
      if (discountRow) discountRow.remove();
    }

    // Handle gift cards (for gift card payment method)
    if (giftCards && giftCards.totalAmount) {
      const giftCardAmount = parseFloat(giftCards.totalAmount);
      if (giftCardAmount > 0) {
        const giftCardRow = document.querySelector('.order-callback__row.giftcard');
        if (giftCardRow) {
          const giftCardField = giftCardRow.querySelector('[data-field="summary-giftcard"]');
          if (giftCardField) {
            giftCardField.textContent = '-' + Shopify.formatMoney(this.toCents(giftCardAmount), window.theme.moneyFormat);
          }
        }
      } else {
        const giftCardRow = document.querySelector('.order-callback__row.giftcard');
        if (giftCardRow) giftCardRow.remove();
      }
    } else {
      const giftCardRow = document.querySelector('.order-callback__row.giftcard');
      if (giftCardRow) giftCardRow.remove();
    }

    this.setField('summary-total', Shopify.formatMoney(this.toCents(pricing.total), window.theme.moneyFormat));
  }



  render(payload) {
    this.ensureMounted();
    const order = payload.order || {};
    const orderName = payload.orderNumber || order.name || '';
    this.setField('order-number', orderName);
    this.setField('order-date', this.fmtDate(payload.orderDate));
    this.setField('order-email', order.email || '');

    const items = Array.isArray(order.lineItems) ? order.lineItems : [];
    this.renderLineItems(items);
    this.renderAddress(order.shippingAddress, order.customer);
    
    // Calculate proper subtotal from line items (before discounts)
    const calculatedSubtotal = this.calculateSubtotal(items);
    const pricing = order.pricing || { total: '0.00', subtotal: '0.00', shipping: '0.00', tax: '0.00', currency: 'USD' };
    
    // Override subtotal with calculated value if we have items
    if (items.length > 0 && calculatedSubtotal > 0) {
      pricing.subtotal = (calculatedSubtotal / 100).toFixed(2);
    }
    
    this.renderSummary(
      pricing,
      order.discounts || null,
      order.giftCards || null
    );
    
    // Trustpilot embedded review form takes precedence over the internal survey
    // when the theme setting is on. The container only exists in that mode.
    const trustpilotCard = document.querySelector('[data-survey-card][data-trustpilot-mode]');
    if (trustpilotCard) {
      this.initTrustpilotEmbed(payload, trustpilotCard);
      return;
    }

    // Pre-fill survey fields if available
    const unmaskedEmail = payload.contactEmail || order.customer?.email || order.billingAddress?.email || order.email;
    const customerName = this.getCustomerName(order);
    this.prefillSurveyFields(unmaskedEmail, customerName);

    // Initialize survey form
    this.initSurveyForm();
  }

  initTrustpilotEmbed(payload, card) {
    if (this._trustpilotConsentListener) {
      document.removeEventListener('hd:consent-changed', this._trustpilotConsentListener);
      this._trustpilotConsentListener = null;
    }
    const widgetContainer = card.querySelector('[data-trustpilot-embed]');
    const unavailable = card.querySelector('[data-trustpilot-unavailable]');
    const collector = payload && payload.trustpilotReviewCollector;

    if (!collector || !collector.payload || !widgetContainer) {
      // No Trustpilot collector config for this order — show fallback.
      card.hidden = false;
      if (widgetContainer) widgetContainer.hidden = true;
      if (unavailable) unavailable.hidden = false;
      return;
    }

    // Trustpilot Verified Review Collector web component. The widget is loaded
    // from reviewcollection.trustpilot.com and manages its own cookies. We still
    // gate on Shopify's shared permission state so nothing third-party runs
    // without consent.
    let visible = false;
    this._trustpilotConsentListener = () => {
      if (!this.hasPreferencesConsent()) {
        card.hidden = true;
        widgetContainer.querySelectorAll('trustbox-verified-review-collector').forEach(widget => widget.remove());
        visible = false;
        return;
      }
      this.renderTrustpilotWidget(widgetContainer, collector);
      card.hidden = false;
      if (!visible) this.trackTrustpilotEvent('trustpilot_review_section_shown');
      visible = true;
    };
    document.addEventListener('hd:consent-changed', this._trustpilotConsentListener);
    this._trustpilotConsentListener();
  }

  trackTrustpilotEvent(event) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event });
    } catch (_) {}
  }

  hasPreferencesConsent() {
    try {
      const state = window.HappyDayConsent?.getState();
      return state?.ready === true && state.preferencesAllowed === true;
    } catch (_) {
      return false;
    }
  }

  renderTrustpilotWidget(container, collector) {
    container.hidden = false;
    if (container.querySelector('trustbox-verified-review-collector')) return;

    this.loadTrustpilotCollectorScript();

    const appendWidget = () => {
      // A visitor can revoke consent while this component is downloading.
      if (!this.hasPreferencesConsent() || container.querySelector('trustbox-verified-review-collector')) return;
      const widget = document.createElement('trustbox-verified-review-collector');
      widget.id = 'tp-verified-widget';
      widget.className = 'trustpilot-widget';
      widget.setAttribute('style-size', 'M');
      widget.setAttribute('style-width', '100%');
      widget.setAttribute('border-type', 'noBorder');
      widget.setAttribute('background', 'light');
      widget.setAttribute('has-animated-stars', 'true');
      widget.setAttribute('tags', 'purchase');
      if (collector.locale) widget.setAttribute('locale', collector.locale);
      if (collector.templateId) widget.setAttribute('template-id', collector.templateId);
      if (collector.businessUnitId) widget.setAttribute('businessunit-id', collector.businessUnitId);

      const dispatchPayload = () => {
        if (!this.hasPreferencesConsent()) return;
        console.log('[Trustpilot] dispatching customerDataResponse');
        widget.dispatchEvent(new CustomEvent('customerDataResponse', {
          detail: collector.payload,
        }));
      };

      // The widget exposes an onReady lifecycle hook. Trustpilot's docs show it
      // as a property callback. Some builds also emit a 'ready' DOM event —
      // wire both so whichever the script uses, we catch it.
      widget.onReady = () => {
        console.log('[Trustpilot] onReady called');
        dispatchPayload();
      };
      widget.addEventListener('ready', () => {
        console.log('[Trustpilot] ready event fired');
        dispatchPayload();
      });

      container.appendChild(widget);
    };

    // Wait until the custom element is actually registered before creating it,
    // so our onReady assignment lands on an upgraded instance (not an
    // HTMLUnknownElement whose expando gets shadowed during upgrade).
    if (window.customElements && typeof customElements.whenDefined === 'function') {
      customElements.whenDefined('trustbox-verified-review-collector')
        .then(appendWidget)
        .catch((err) => console.warn('[Trustpilot] whenDefined rejected', err));
    } else {
      appendWidget();
    }
  }

  loadTrustpilotCollectorScript() {
    if (document.querySelector('script[data-trustpilot-collector]')) return;
    const script = document.createElement('script');
    script.src = 'https://reviewcollection.trustpilot.com/verifiedreviewcollector/main.js';
    script.async = true;
    script.setAttribute('data-trustpilot-collector', '');
    document.head.appendChild(script);
  }
  
  getCustomerName(order) {
    // Try to get name from multiple sources
    const customer = order.customer || {};
    const shippingAddress = order.shippingAddress || {};
    const billingAddress = order.billingAddress || {};
    
    // Build full name from first and last name
    const firstName = customer.firstName || shippingAddress.firstName || billingAddress.firstName || '';
    const lastName = customer.lastName || shippingAddress.lastName || billingAddress.lastName || '';
    
    // Return combined name if we have at least one part
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || '';
  }
  
  prefillSurveyFields(email, name) {
    // Prefill email
    const emailInput = document.querySelector('[data-field="survey-email-input"]');
    if (emailInput && email) {
      // Don't prefill if email contains asterisks (masked email)
      if (!email.includes('*')) {
        emailInput.value = email;
      }
    }
    
    // Prefill name
    const nameInput = document.querySelector('[data-field="survey-name-input"]');
    if (nameInput && name) {
      nameInput.value = name;
    }
  }
  
  initSurveyForm() {
    const form = document.querySelector('[data-survey-form]');
    if (!form) return;
    
    // Check if survey was already submitted for this order
    this.checkSurveyStatus();
    
    // Initialize star rating interaction
    this.initStarRating();
    
    // Handle form submission
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.handleSurveySubmit(form);
    });
  }
  
  async checkSurveyStatus() {
    if (!this.token) return;
    
    try {
      const endpoint = `${window.proxyPath}/survey/check/${this.token}`;
      const response = await window.apiService.get(endpoint);
      
      // If no survey exists, show the form
      if (response && !response.exists) {
        this.showSurveySection();
      }
      // If survey exists, keep it hidden (default state)
    } catch (error) {
      // If we get a 404 or error indicating no survey, show the form
      if (error && (error.code === 404 || !error.exists)) {
        this.showSurveySection();
      }
      // If 409 or exists flag, keep hidden
      console.log('Survey status check:', error?.message || 'Survey check complete');
    }
  }
  
  showSurveySection() {
    const surveyCard = document.querySelector('[data-survey-card]');
    if (surveyCard) {
      surveyCard.hidden = false;
    }
  }
  
  hideSurveySection() {
    const surveyCard = document.querySelector('[data-survey-card]');
    if (surveyCard) {
      surveyCard.hidden = true;
    }
  }
  
  initStarRating() {
    const ratingContainer = document.querySelector('[data-rating-container]');
    if (!ratingContainer) return;
    
    const labels = ratingContainer.querySelectorAll('.order-callback__star-label');
    
    labels.forEach((label) => {
      label.addEventListener('mouseenter', () => {
        const rating = parseInt(label.dataset.rating);
        this.highlightStars(rating);
      });
    });
    
    ratingContainer.addEventListener('mouseleave', () => {
      const checkedInput = ratingContainer.querySelector('input[name="rating"]:checked');
      if (checkedInput) {
        this.highlightStars(parseInt(checkedInput.value));
      } else {
        this.highlightStars(0);
      }
    });
    
    // Update stars when rating is selected
    const inputs = ratingContainer.querySelectorAll('input[name="rating"]');
    inputs.forEach(input => {
      input.addEventListener('change', () => {
        this.highlightStars(parseInt(input.value));
      });
    });
  }
  
  highlightStars(rating) {
    const labels = document.querySelectorAll('.order-callback__star-label');
    labels.forEach((label) => {
      const starRating = parseInt(label.dataset.rating);
      if (starRating <= rating) {
        label.classList.add('is-filled');
      } else {
        label.classList.remove('is-filled');
      }
    });
  }
  
  async handleSurveySubmit(form) {
    const submitButton = form.querySelector('[data-survey-submit]');
    const thankYouSection = document.querySelector('[data-thank-you]');
    
    // Disable submit button
    submitButton.disabled = true;
    submitButton.textContent = this.getTranslation('customer.order.callback.survey_submitting', 'Submitting...');
    
    try {
      // Get form data
      const formData = new FormData(form);
      const data = {
        name: formData.get('name'),
        email: formData.get('email'),
        rating: formData.get('rating'),
        message: formData.get('message'),
        token: this.token,
        timestamp: new Date().toISOString()
      };
      
      // Submit to your API endpoint (you'll need to create this endpoint)
      const endpoint = `${window.proxyPath}/survey`;
      
      const response = await window.apiService.post(endpoint, data);
      
      if (response && response.success !== false) {
        const rating = parseInt(data.rating);
        console.log('Survey submitted with rating:', rating, '(type:', typeof rating, ')');
        
        // Hide survey headings and form
        const surveyHeadings = document.querySelectorAll('[data-survey-heading]');
        surveyHeadings.forEach(heading => heading.style.display = 'none');
        form.style.display = 'none';
        
        if (rating >= 4) {
          // High rating (4-5): Show review invite
          console.log('Rating is 4 or 5, showing review invite...');
          this.showReviewInvite();
        } else {
          // Low rating (1-3): Show thank you message
          console.log('Rating is less than 4, showing thank you');
          if (thankYouSection) {
            thankYouSection.hidden = false;
          }
        }
      } else {
        throw new Error('Survey submission failed');
      }
      
    } catch (error) {
      console.error('Survey submission error:', error);
      
      // Parse error response for meaningful messages
      let errorMessage = this.getTranslation('customer.order.callback.survey_error', 'Failed to submit survey. Please try again.');
      
      if (error && typeof error === 'object') {
        // Check if it's an API response with error details
        if (error.code === 409 || error.message?.toLowerCase().includes('already submitted')) {
          errorMessage = this.getTranslation('customer.order.callback.survey_already_submitted', 'You have already submitted a review for this order.');
        } else if (error.code === 400 || error.message?.toLowerCase().includes('validation')) {
          errorMessage = this.getTranslation('customer.order.callback.survey_validation_error', 'Please check your information and try again.');
        } else if (error.code === 401 || error.code === 403) {
          errorMessage = this.getTranslation('customer.order.callback.survey_unauthorized', 'Unable to verify your order. Please try again.');
        } else if (error.message) {
          // Use the message from the API if available
          errorMessage = error.message;
        }
      }
      
      // Show error message
      try {
        if (window.ErrorUtils && typeof window.ErrorUtils.toast === 'function') {
          window.ErrorUtils.showError(errorMessage);
        } else {
          alert(errorMessage);
        }
      } catch (_) {
        alert(errorMessage);
      }
      
      // Re-enable submit button
      submitButton.disabled = false;
      submitButton.textContent = this.getTranslation('customer.order.callback.survey_submit', 'Submit review');
    }
  }

  showReviewInvite() {
    console.log('showReviewInvite() called');
    
    // Hide the survey headings (already hidden in handleSurveySubmit, but ensure)
    const surveyHeadings = document.querySelectorAll('[data-survey-heading]');
    surveyHeadings.forEach(heading => {
      heading.style.display = 'none';
    });
    
    // Hide the survey form (already hidden in handleSurveySubmit, but ensure)
    const surveyForm = document.querySelector('[data-survey-form]');
    if (surveyForm) {
      surveyForm.style.display = 'none';
    }
    
    // Hide thank you section if visible
    const thankYouSection = document.querySelector('[data-thank-you]');
    if (thankYouSection) {
      thankYouSection.hidden = true;
    }
    
    const reviewInvite = document.querySelector('[data-review-invite]');
    
    if (!reviewInvite) {
      console.error('Review invite element not found! Looking for [data-review-invite]');
      return;
    }
    
    console.log('Review invite element found, removing hidden attribute and ensuring visibility');
    
    // Remove hidden attribute and explicitly set display
    reviewInvite.hidden = false;
    reviewInvite.removeAttribute('hidden');
    reviewInvite.style.display = 'block';
    
    // Log computed styles for debugging
    const computedStyle = window.getComputedStyle(reviewInvite);
    console.log('Review invite computed display:', computedStyle.display);
    console.log('Review invite computed visibility:', computedStyle.visibility);
    console.log('Review invite offsetHeight:', reviewInvite.offsetHeight);
    
    // Scroll into view so user sees it
    reviewInvite.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    
    // Initialize review form
    const reviewForm = document.querySelector('[data-review-form]');
    if (reviewForm) {
      console.log('Review form found, attaching submit handler');
      reviewForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        await this.handleReviewInviteSubmit(reviewForm);
      });
    } else {
      console.error('Review form not found! Looking for [data-review-form]');
    }
  }

  async handleReviewInviteSubmit(form) {
    const submitButton = form.querySelector('[data-review-submit]');
    
    // Disable submit button
    submitButton.disabled = true;
    const originalText = submitButton.textContent;
    submitButton.textContent = this.getTranslation('customer.order.callback.survey_submitting', 'Submitting...');
    
    try {
      const formData = new FormData(form);
      
      // Get checkbox values - they need to be checked to return 'true'
      const googleCheckbox = form.querySelector('input[name="google_review"]');
      const trustpilotCheckbox = form.querySelector('input[name="trustpilot_review"]');
      
      const data = {
        token: this.token,
        wantsGoogleReview: googleCheckbox ? googleCheckbox.checked : false,
        wantsTrustpilotReview: trustpilotCheckbox ? trustpilotCheckbox.checked : false
      };
      
      console.log('Submitting review preferences:', data);
      
      // Use Shopify app proxy path, same as survey submission
      const endpoint = `${window.proxyPath}/survey/${this.token}`;
      
      // Use PUT method through apiService if available, otherwise fetch
      let result;
      if (window.apiService && typeof window.apiService.put === 'function') {
        result = await window.apiService.put(endpoint, data);
      } else {
        // Fallback to fetch with PUT
        const response = await fetch(endpoint, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify(data)
        });
        result = await response.json();
      }
      
      if (result && result.success !== false) {
        console.log('Review preferences updated successfully');
        
        // Hide the review invite section
        const reviewInvite = document.querySelector('[data-review-invite]');
        if (reviewInvite) {
          reviewInvite.style.display = 'none';
        }
        
        // Show thank you state
        const thankYouSection = document.querySelector('[data-thank-you]');
        if (thankYouSection) {
          thankYouSection.hidden = false;
        }
      } else {
        throw new Error(result.message || 'Review preference submission failed');
      }
      
    } catch (error) {
      console.error('Review preference error:', error);
      
      let errorMessage = this.getTranslation('customer.order.callback.review_invite_error', 'Failed to send invitation. Please try again.');
      if (error && error.message) {
        errorMessage = error.message;
      }
      
      try {
        if (window.ErrorUtils && typeof window.ErrorUtils.showError === 'function') {
          window.ErrorUtils.showError(errorMessage);
        } else {
          alert(errorMessage);
        }
      } catch (_) {
        alert(errorMessage);
      }
      
      submitButton.disabled = false;
      submitButton.textContent = originalText;
    }
  }
  
  calculateSubtotal(items) {
    if (!Array.isArray(items) || items.length === 0) return 0;
    
    let subtotal = 0;
    items.forEach(item => {
      // Use unitPrice (before discount) * quantity, not totalPrice (after discount)
      const unitPrice = parseFloat(item.unitPrice || item.totalPrice || 0);
      const quantity = parseInt(item.quantity || 1);
      subtotal += this.toCents(unitPrice) * quantity;
    });
    
    return subtotal;
  }

  async fetchAndRender() {
    const maxRetries = 10; // Try for up to 10 times
    const retryInterval = 3000; // 3 seconds between retries
    let attempt = 0;
    
    while (attempt < maxRetries) {
      try {
        const endpoint = `${window.proxyPath}/callback/${this.token}`;
        const data = await window.apiService.get(endpoint);
        
        // Check for specific error cases that might be timing-related
        if (data && data.success === false) {
          // For backend errors like INVALID_TOKEN, continue retrying as it might be timing
          attempt++;
          if (attempt < maxRetries) {
            this.updateLoadingMessage(attempt, maxRetries);
            await this.delay(retryInterval);
            continue;
          } else {
            break; // Last attempt failed
          }
        }
        
        // Check if we got valid data (not null/empty)
        if (this.isValidOrderData(data)) {
          console.log('Order Callback: Valid order data received, rendering...');
          this.hideLoadingState();
          this.render(data);
          
          console.log('Order Callback: Render complete, preparing to track conversion...');
          
          // Track purchase conversion only after successful data load and render
          const order = data.order || {};
          const items = Array.isArray(order.lineItems) ? order.lineItems : [];
          const calculatedSubtotal = this.calculateSubtotal(items);
          const pricing = order.pricing || { total: '0.00', subtotal: '0.00', shipping: '0.00', tax: '0.00', currency: 'USD' };
          
          console.log('Order Callback: Conversion data prepared', {
            orderName: order.name,
            itemCount: items.length,
            total: pricing.total,
            currency: pricing.currency
          });
          
          // Override subtotal with calculated value if we have items
          if (items.length > 0 && calculatedSubtotal > 0) {
            pricing.subtotal = (calculatedSubtotal / 100).toFixed(2);
          }
          
          console.log('Order Callback: Calling trackPurchaseConversion...');
          await this.trackPurchaseConversion(data, items, pricing);
          console.log('Order Callback: trackPurchaseConversion completed');
          return; // Success - exit retry loop
        }
        
        // Data is null/empty, wait and retry
        attempt++;
        if (attempt < maxRetries) {
          this.updateLoadingMessage(attempt, maxRetries);
          await this.delay(retryInterval);
        }
      } catch (err) {
        attempt++;
        if (attempt < maxRetries) {
          await this.delay(retryInterval);
        }
      }
    }
    
    // All retries exhausted
    this.hideLoadingState();
    try {
      ErrorUtils.toast('customer.order.callback', 'error_loading', 'Could not load order details. The order may still be processing. Please check your email for confirmation or contact support.');
    } catch (_) {
      console.error('Order Callback: Failed to load order data after all retries');
    }
  }
  
  isValidOrderData(data) {
    // Check if we have valid order data
    return data && 
           data.order && 
           (data.order.name || data.order.orderNumber) && 
           Array.isArray(data.order.lineItems) &&
           data.order.lineItems.length > 0;
  }
  
  showLoadingState() {
    const root = this.ensureMounted();
    if (!root) return;
    
    // Add loading class to root
    root.classList.add('order-callback--loading');
    
    // Find main content and add loading overlay
    const mainContent = root.querySelector('.order-callback__main');
    const aside = root.querySelector('.order-callback__aside');
    
    if (mainContent) {
      this.addLoadingOverlay(mainContent);
    }
    if (aside) {
      this.addLoadingOverlay(aside);
    }
  }
  
  hideLoadingState() {
    const root = this.root;
    if (!root) return;
    
    root.classList.remove('order-callback--loading');
    
    // Remove loading overlays
    const overlays = root.querySelectorAll('.order-callback__loading-overlay');
    overlays.forEach(overlay => overlay.remove());
  }
  
  addLoadingOverlay(container) {
    // Don't add if already exists
    if (container.querySelector('.order-callback__loading-overlay')) return;
    
    const loadingText = this.getTranslation('customer.order.callback.loading_order_details', 'Loading your order details...');
    
    const overlay = document.createElement('div');
    overlay.className = 'order-callback__loading-overlay';
    overlay.innerHTML = `
      <div class="order-callback__loading-content">
        <div class="order-callback__spinner"></div>
        <p class="u-p3">${this.escapeHtml(loadingText)}</p>
      </div>
    `;
    
    container.style.position = 'relative';
    container.appendChild(overlay);
  }
  
  getTranslation(key, fallback) {
    try {
      // Try TranslationUtils first
      if (window.TranslationUtils && typeof window.TranslationUtils.get === 'function') {
        return window.TranslationUtils.get(key, fallback);
      }
      
      // Fallback to window.translations
      if (window.translations) {
        const keys = key.split('.');
        let value = window.translations;
        for (const k of keys) {
          if (value && typeof value === 'object' && k in value) {
            value = value[k];
          } else {
            return fallback;
          }
        }
        return typeof value === 'string' ? value : fallback;
      }
    } catch (_) {}
    return fallback;
  }
  
  updateLoadingMessage() {
    const root = this.root;
    if (!root) return;
    
    const loadingTexts = root.querySelectorAll('.order-callback__loading-overlay p');
    const baseText = this.getTranslation('customer.order.callback.loading_order_details', 'Loading your order details...');

    loadingTexts.forEach(el => {
      el.textContent = baseText;
    });
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  parseMoneyAmount(value) {
    if (value === null || typeof value === 'undefined') return 0;
    if (typeof value === 'number') return isNaN(value) ? 0 : value;
    if (typeof value === 'object') {
      if (typeof value.amount !== 'undefined') return this.parseMoneyAmount(value.amount);
      if (typeof value.value !== 'undefined') return this.parseMoneyAmount(value.value);
      if (typeof value.totalAmount !== 'undefined') return this.parseMoneyAmount(value.totalAmount);
    }

    let text = String(value).trim().replace(/\s/g, '');
    if (!text) return 0;
    if (text.includes(',') && !text.includes('.')) {
      text = text.replace(',', '.');
    } else {
      text = text.replace(/,/g, '');
    }

    const parsed = parseFloat(text.replace(/[^\d.-]/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  }

  getGiftCardPaymentAmount(order) {
    const amounts = [];
    const addAmount = (value) => {
      const amount = this.parseMoneyAmount(value);
      if (amount > 0) amounts.push(amount);
    };

    const giftCards = order?.giftCards;
    addAmount(giftCards?.totalAmount);
    addAmount(giftCards?.amount);
    addAmount(giftCards?.amountUsed);
    addAmount(order?.giftCardAmount);
    addAmount(order?.gift_card_amount);
    addAmount(order?.payment?.giftCardAmount);

    const collectGiftCardList = (list) => {
      if (!Array.isArray(list)) return;
      list.forEach(card => {
        addAmount(card?.amountUsed?.amount);
        addAmount(card?.amountUsed);
        addAmount(card?.amount);
        addAmount(card?.totalAmount);
        addAmount(card?.applied);
      });
    };

    collectGiftCardList(Array.isArray(giftCards) ? giftCards : null);
    collectGiftCardList(giftCards?.items);
    collectGiftCardList(giftCards?.cards);
    collectGiftCardList(giftCards?.appliedGiftCards);
    collectGiftCardList(giftCards?.giftCards);

    const discountDescription = [
      order?.discounts?.description,
      order?.discounts?.title,
      order?.discounts?.code
    ].filter(Boolean).join(' ').toLowerCase();

    if (
      discountDescription.includes('applied gift card') ||
      discountDescription.includes('applied gift cards') ||
      discountDescription.includes('gift cards:')
    ) {
      addAmount(order?.discounts?.totalAmount);
    }

    return amounts.length > 0 ? Math.max(...amounts) : 0;
  }

  hasGiftCardPayment(order) {
    if (this.getGiftCardPaymentAmount(order) > 0) return true;

    const giftCards = order?.giftCards;
    if (Array.isArray(giftCards) && giftCards.length > 0) return true;
    if (Array.isArray(giftCards?.items) && giftCards.items.length > 0) return true;
    if (Array.isArray(giftCards?.cards) && giftCards.cards.length > 0) return true;
    if (Array.isArray(giftCards?.appliedGiftCards) && giftCards.appliedGiftCards.length > 0) return true;

    const discountDescription = [
      order?.discounts?.description,
      order?.discounts?.title,
      order?.discounts?.code
    ].filter(Boolean).join(' ').toLowerCase();

    return discountDescription.includes('applied gift card') ||
      discountDescription.includes('applied gift cards') ||
      discountDescription.includes('gift cards:');
  }

  getBackendReportingEventName(payload, order) {
    const candidates = [
      payload?.reportingEvent,
      payload?.reportingEventName,
      payload?.analyticsEvent,
      payload?.analyticsEventName,
      payload?.conversionEvent,
      payload?.eventName,
      order?.reportingEvent,
      order?.reportingEventName,
      order?.analyticsEvent,
      order?.analyticsEventName,
      order?.conversionEvent,
      order?.eventName,
      order?.reportingType,
      order?.orderType
    ];

    for (const candidate of candidates) {
      if (!candidate) continue;
      const normalized = String(candidate).toLowerCase().trim().replace(/-/g, '_').replace(/^s_/, '');
      if (normalized === 'purchase') return 'purchase';
      if (normalized.includes('gift_card_redemption') || normalized.includes('giftcard_redemption')) return 'gift_card_redemption';
      if (normalized.includes('exchange')) return 'exchange';
      if (normalized.includes('extension') || normalized.includes('voucher_extension')) return 'extension';
    }

    return '';
  }

  getOrderReportingEventName(payload, order, items) {
    const backendEventName = this.getBackendReportingEventName(payload, order);
    if (backendEventName) return backendEventName;

    const extendTypes = (Array.isArray(items) ? items : [])
      .map(item => String(this.getLineItemAttributes(item)['_extend_type'] || '').toLowerCase());

    if (extendTypes.some(type => type.includes('voucher_exchange') || type === 'exchange')) {
      return 'exchange';
    }

    if (extendTypes.some(type => type.includes('voucher_extension') || type === 'extension')) {
      return 'extension';
    }

    if (this.hasGiftCardPayment(order)) {
      return 'gift_card_redemption';
    }

    return backendEventName || 'purchase';
  }

  getReportingValue(eventName, total, giftCardAmount) {
    if (eventName !== 'gift_card_redemption') return total;
    return Math.round((total + giftCardAmount) * 100) / 100;
  }

  async waitForGTM(maxWait = 3000) {
    const start = Date.now();
    
    // GTM uses dataLayer, not gtag - so just wait a bit for GTM to initialize
    console.log('Order Callback: Waiting for GTM to initialize...');
    
    // Wait a short time for GTM's dataLayer to be ready
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const hasGtag = typeof gtag !== 'undefined';
    const hasDataLayer = typeof window.dataLayer !== 'undefined';
    
    if (hasGtag) {
      console.log('Order Callback: gtag is available');
    }
    if (hasDataLayer) {
      console.log('Order Callback: dataLayer is ready (GTM mode)');
    }
    
    return hasGtag || hasDataLayer;
  }

  async trackPurchaseConversion(payload, items, pricing) {
    console.log('Order Callback: trackPurchaseConversion STARTED');
    
    // Check if tracking is disabled via toggle
    if (!PURCHASE_EVENT_TRACKING_ENABLED) {
      console.log('Order Callback: Purchase event tracking is DISABLED via toggle');
      return;
    }

    // Note: We fire the conversion regardless of consent state.
    // Google Ads handles consent via gcs/gcd parameters in the request.
    // Server-side SGTM also sends this conversion, so both sides stay consistent.

    // Only fire frontend conversion on the callback page with a valid order number
    const isCallbackPage = window.orderCallbackMode === true || window.location.pathname.split('/').includes('callback');
    const order = payload.order || {};
    const orderNumber = payload.orderNumber || order.name || order.orderNumber || '';
    if (!orderNumber || !isCallbackPage) {
      console.log('Order Callback: Skipping frontend conversion — missing order number or not on callback page', { orderNumber, isCallbackPage });
      return;
    }
    const reportingEventName = this.getOrderReportingEventName(payload, order, items);
    const serverEventName = reportingEventName === 'purchase' ? 's_purchase' : `s_${reportingEventName}`;

    // Only run conversion tracking on production store
    const currentShop = window.Shopify?.shop || '';
    const isProduction = currentShop === 'happy-day-prod.myshopify.com';
    
    if (!isProduction) {
      console.log('Order Callback: Not on production store, skipping tracking');
      return;
    }
    

    
    try {
      // Use a dedicated tracking flag to prevent duplicate conversion fires
      const trackingFlagKey = this.token ? `order-tracked:${this.token}` : null;
      if (!trackingFlagKey) {
        console.warn('Order Callback: No token available for tracking');
        return;
      }

      const alreadyTracked = window.localStorage && localStorage.getItem(trackingFlagKey) === '1';

      console.log('Order Callback: Checking if already tracked...', {
        trackingFlagKey: trackingFlagKey,
        alreadyTracked: alreadyTracked
      });

      if (alreadyTracked) {
        console.log('Order Callback: Conversion already tracked for this order, skipping');
        return;
      }

      console.log(`Order Callback: First load - firing ${reportingEventName} tracking event...`);

      // Wait for gtag to be ready
      await this.waitForGTM();

      const transactionId = orderNumber.replace(/^#/, '');

      // Parse pricing values
      const total = this.parseMoneyAmount(pricing.total);
      const currency = pricing.currency || window.theme?.shopCurrency || 'SEK';
      const giftCardAmount = this.getGiftCardPaymentAmount(order);
      const cashPaidAmount = Math.max(0, Math.round(total * 100) / 100);
      const reportingValue = this.getReportingValue(reportingEventName, total, giftCardAmount);

      // Extract user information
      const customer = order.customer || {};
      const shippingAddress = order.shippingAddress || {};
      
      // Get user name from customer or shipping address
      const userFirstName = customer.firstName || shippingAddress.firstName || '';
      const userLastName = customer.lastName || shippingAddress.lastName || '';
      
      const userEmail = order.email || customer.email || '';
      const userPhone = shippingAddress.phone || customer.phone || '';
      
      // Get address information
      const streetAddress = shippingAddress.address1 || '';
      const city = shippingAddress.city || '';
      const region = shippingAddress.province || shippingAddress.provinceCode || '';
      const postalCode = shippingAddress.zip || '';
      const rawCountry = shippingAddress.countryCodeV2 || shippingAddress.countryCode || shippingAddress.country || '';
      // Ensure 2-letter ISO code — Shopify sometimes returns full name
      const country = rawCountry.length === 2 ? rawCountry : this.countryToISO(rawCountry);
      
      // Normalize phone to E.164 format before hashing
      const normalizedPhone = this.normalizePhone(userPhone);
      
      // Hash user data for enhanced conversions (Google Ads requirement)
      // Uses per-field normalization matching server-side userDataHashing.utils.js
      // Only include fields that have actual data — empty strings cause "missing data" warnings
      const hashedEmail = await this.hashEmail(userEmail);
      const hashedPhone = await this.sha256(normalizedPhone);
      const hashedFirstName = await this.hashName(userFirstName);
      const hashedLastName = await this.hashName(userLastName);
      const hashedStreet = await this.hashStreet(streetAddress);

      const hashedUserData = {};
      if (hashedEmail) hashedUserData.sha256_email_address = hashedEmail;
      if (hashedPhone) hashedUserData.sha256_phone_number = hashedPhone;

      const addressData = {};
      if (hashedFirstName) addressData.sha256_first_name = hashedFirstName;
      if (hashedLastName) addressData.sha256_last_name = hashedLastName;
      if (hashedStreet) addressData.sha256_street = hashedStreet;
      if (city.trim()) addressData.city = city.trim();
      if (region.trim()) addressData.region = region.trim();
      if (postalCode.trim()) addressData.postal_code = postalCode.trim();
      if (country.trim()) addressData.country = country.toUpperCase().trim();

      if (Object.keys(addressData).length > 0) {
        hashedUserData.address = addressData;
      }



      // Google Ads conversions now handled via GTM → sGTM
      // Direct gtag calls removed; GTM/sGTM decides which events map to Google Ads.
      const hasUserData = hashedUserData.sha256_email_address || hashedUserData.sha256_phone_number;

      // GTM: GA4 reporting event with full ecommerce object + items.
      if (window.dataLayer && Array.isArray(items) && items.length > 0) {
        const purchaseItems = items.map((item, index) => {
          const qty = item.quantity || 1;
          const rawUnit = parseFloat(item.unitPrice);
          const rawTotal = parseFloat(item.totalPrice);
          // Use unitPrice (before discount) as the GA4 price; fall back to totalPrice/qty
          const unitPrice = !isNaN(rawUnit) ? rawUnit : (!isNaN(rawTotal) ? rawTotal / qty : 0);
          const price = Math.round(unitPrice * 100) / 100;
          // Calculate per-unit discount: unitPrice - (totalPrice / qty)
          const effectivePerUnit = !isNaN(rawTotal) ? rawTotal / qty : unitPrice;
          const discountPerUnit = Math.round((unitPrice - effectivePerUnit) * 100) / 100;
          const entry = {
            item_id: (item.variant?.id || item.product?.id || '').toString().replace(/^gid:\/\/shopify\/\w+\//, ''),
            item_name: item.title || item.product?.title || '',
            item_brand: item.vendor || '',
            item_variant: item.variant?.title || 'Default Title',
            price: price,
            quantity: qty,
            index: index + 1
          };
          if (discountPerUnit > 0) entry.discount = discountPerUnit;
          return entry;
        }).filter(entry => reportingEventName !== 'purchase' || entry.price !== 0);
        // WHY: free accessories (e.g. packaging) carry no paid value, so keep them out of
        // paid purchase reporting. Other reporting events keep their items unchanged.

        const trackingEvent = {
          event: reportingEventName,
          server_event_name: serverEventName,
          page_location: window.location.origin + '/cart/' + transactionId,
          ecommerce: {
            transaction_id: transactionId,
            value: reportingValue,
            tax: this.parseMoneyAmount(pricing.tax),
            shipping: this.parseMoneyAmount(pricing.shipping),
            currency: currency,
            items: purchaseItems
          }
        };

        if (giftCardAmount > 0) {
          trackingEvent.ecommerce.gift_card_amount = giftCardAmount;
          trackingEvent.ecommerce.cash_paid_amount = cashPaidAmount;
        }

        if (reportingEventName !== 'purchase') {
          trackingEvent.ecommerce.reporting_event = reportingEventName;
        }

        // Hashing is asynchronous; re-read current permission at dispatch so a revocation wins.
        let matchingAllowed = false;
        try {
          const consent = window.HappyDayConsent?.getState();
          matchingAllowed = consent?.ready === true && consent.marketingAllowed === true;
        } catch (_) { /* An unavailable purpose choice cannot authorize customer matching. */ }
        if (hasUserData && matchingAllowed) {
          trackingEvent.enhanced_conversions = hashedUserData;
        }

        dataLayer.push({ ecommerce: null });
        dataLayer.push(trackingEvent);
        console.log(`Order Callback: GA4 ${reportingEventName} event pushed to dataLayer`);
      }


      // Mark as tracked to prevent duplicate fires on page reload
      try { if (window.localStorage) localStorage.setItem(trackingFlagKey, '1'); } catch (_) {}
      console.log('Order Callback: Conversion tracking complete (tracked flag set)');

    } catch (error) {
      console.error('Order Callback: Error tracking purchase conversion:', error);
    }
  }

  async clearCartIfNeeded() {
    try {
      if (!this.token || !this.clearFlagKey) return;
      try {
        if (window.localStorage && localStorage.getItem(this.clearFlagKey) === '1') {
          console.log('Order Callback: Cart already cleared for this token');
          return; // already cleared for this token
        }
      } catch (_) {}

      console.log('Order Callback: Starting cart and cookie clear');

      // Clear Shopify cart
      if (window.apiService && typeof window.apiService.post === 'function') {
        await window.apiService.post('/cart/clear.js');
      } else {
        await fetch('/cart/clear.js', { method: 'POST', credentials: 'same-origin' });
      }

      // Clear cart-related cookies
      const cookiesToClear = ['cart', 'cart_token', 'cart_ts', 'cart_ver'];
      cookiesToClear.forEach(cookie => {
        document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
        document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
      });

      // Update window.cart
      window.cart = {
        token: null,
        note: '',
        attributes: {},
        total_price: 0,
        total_weight: 0,
        item_count: 0,
        items: [],
        requires_shipping: false,
        currency: window.cart?.currency || 'USD'
      };

      // Trigger cart update events
      if (typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart: window.cart }
        }));
      }

      console.log('Order Callback: Cart and cookies cleared successfully');

      try { if (window.localStorage) localStorage.setItem(this.clearFlagKey, '1'); } catch (_) {}
      // Clear Qliro payment pending flag so the stale-payment cleanup doesn't double-clear
      try { localStorage.removeItem('qliroPaymentPending'); } catch (_) {}
      // Clear exchange/extend voucher data to prevent duplicate gift card creation
      try { localStorage.removeItem('exchangeGiftCard'); } catch (_) {}
      try { localStorage.removeItem('extendGiftCard'); } catch (_) {}

    } catch (e) {
      console.warn('Order Callback: cart clear failed', e);
    }
  }

}

// Boot only on callback/token URLs
(() => {
  const hasCallbackToken = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const t = params.get('t');
      if (t && /^[a-f0-9]{64}$/i.test(t)) return true;
      const parts = window.location.pathname.split('/').filter(Boolean);
      const idx = parts.indexOf('callback');
      if (idx !== -1 && /^[a-f0-9]{64}$/i.test(parts[idx + 1] || '')) return true;
    } catch (_) {}
    return false;
  })();

  if (!hasCallbackToken) return;

  // Immediately cover the page with white overlay to prevent cart flash
  const overlay = document.createElement('div');
  overlay.id = 'callback-overlay';
  overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:#fff;z-index:9999;';
  document.documentElement.appendChild(overlay);

  const start = () => {
    const controller = new OrderCallbackController();
    controller.init();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
